import { useState, useEffect } from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { withRouter } from '../../utils/WithRouter';
import { Box, Button, TextField, Typography } from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import storeyviewLogo from '../../assets/logos/sv_logo_green.png';
import Header from './Header';

import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import TextInput from '../common/formControls/TextInput';
import { useUserQuery, useLogin } from '../../features/auth/authHooks';
import { Navigate } from 'react-router-dom';
import LoadingLogo from '../common/LoadingLogo';
import { theme } from '../../theme';

const CREAM = '#faf7f1';

const Login = (props: any) => {
  const storeDispatch = useAppDispatch();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const client = useApolloClient();
  const { loading, data } = useUserQuery(false);
  const login = useLogin();

  const [userReset] = useMutation(RESETPASSWORD, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Password has been sent. Please check your email.'));
      setForgotPassword(false);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(`${err.toString()}`));
    },
  });

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    login(email, password);
  };

  const handleResetSubmit = (e: any) => {
    e.preventDefault();
    userReset({ variables: { email: email } });
  };

  useEffect(() => {
    client.cache.reset();
    client.resetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <Box
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        '-webkit-transform': 'translate(-50%, -50%)',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <LoadingLogo />
    </Box>
  ) : data?.getUser ? (
    <>
      <Navigate to="/portal" />
    </>
  ) : (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          backgroundColor: CREAM,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '80px',
          padding: 'clamp(30px, 6vh, 140px) 60px',
          flexWrap: 'wrap',
          flex: '1 0 auto',
          '@media (max-width: 900px)': {
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'center',
          },
          '@media (max-width: 600px)': {
            padding: 'clamp(30px, 6vh, 140px) 20px',
          },
        }}
      >
        <Box sx={{ maxWidth: 'clamp(520px, 40vh, 720px)', '@media (max-width: 900px)': { maxWidth: '100%' } }}>
          <Typography sx={{ fontSize: 'clamp(0.75rem, 1.4vh, 1.1rem)', letterSpacing: '2px', color: theme.palette.secondary.main, fontWeight: 600, mb: 1 }}>
            PROPRIETARY SALES PLATFORM
          </Typography>
          <Typography sx={{ fontSize: 'clamp(1.8rem, 4.5vh, 4.5rem)', color: '#1a1a1a', lineHeight: 1.2, mb: 2 }}>
            The platform behind record-setting launches.
          </Typography>
          <Typography sx={{ fontSize: 'clamp(1rem, 1.9vh, 1.5rem)', color: '#1a1a1a', mb: 3 }}>
            Live inventory and pricing, CRM and outreach, customer portals and deal writing — in one workflow. Built
            in-house at RDS and used to sell out communities across the GTA.
          </Typography>
          <Button
            component="a"
            href="https://rdsbrokerage.com/contact/"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              letterSpacing: '1px',
              fontSize: 'clamp(0.75rem, 1.3vh, 1.1rem)',
              padding: 'clamp(6px, 1.2vh, 14px) clamp(16px, 2.5vh, 28px)',
              '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: 'rgba(29,51,40,0.05)' },
            }}
          >
            Request A Demo
          </Button>
        </Box>

        {/* Sign in card */}
        <Box
          id="signin-card"
          sx={{
            width: 'clamp(380px, 32vh, 520px)',
            display: 'flex',
            flexDirection: 'column',
            padding: 'clamp(30px, 4vh, 60px)',
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            textAlign: 'left',
            '@media (max-width: 900px)': {
              width: '100%',
              maxWidth: '380px',
            },
            '@media (max-width: 500px)': {
              maxWidth: '100%',
            },
          }}
        >
          {forgotPassword ? (
            <Box sx={{ marginRight: 'auto', mb: 1 }}>
              <KeyboardReturnIcon onClick={() => setForgotPassword(false)} sx={{ cursor: 'pointer' }} />
            </Box>
          ) : null}
          {!forgotPassword ? (
            <form onSubmit={handleFormSubmit} autoComplete="off">
              <Typography sx={{ fontSize: 'clamp(1.3rem, 2.6vh, 2.2rem)', color: '#1a1a1a', mb: 2 }}>Sign in to your project</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 'clamp(0.7rem, 1.3vh, 1rem)', letterSpacing: '1px', color: '#8a8a8a', mb: 0.5 }}>
                  EMAIL
                </Typography>
                <TextField
                  name={'email'}
                  onChange={(e: any) => setEmail(e.target.value)}
                  value={email}
                  required={true}
                  sx={{
                    width: '100%',
                    backgroundColor: '#f5f4f0',
                    '& .MuiInputBase-input': { fontSize: 'clamp(1rem, 1.8vh, 1.4rem)', padding: 'clamp(16.5px, 2.2vh, 24px) 14px' },
                  }}
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 'clamp(0.7rem, 1.3vh, 1rem)', letterSpacing: '1px', color: '#8a8a8a', mb: 0.5 }}>
                  PASSWORD
                </Typography>
                <TextField
                  type={'password'}
                  name={'password'}
                  onChange={(e: any) => setPassword(e.target.value)}
                  value={password}
                  required={true}
                  sx={{
                    width: '100%',
                    backgroundColor: '#f5f4f0',
                    '& .MuiInputBase-input': { fontSize: 'clamp(1rem, 1.8vh, 1.4rem)', padding: 'clamp(16.5px, 2.2vh, 24px) 14px' },
                  }}
                />
              </Box>
              <Button
                type="submit"
                sx={{
                  width: '100%',
                  marginTop: '20px',
                  backgroundColor: theme.palette.primary.main,
                  letterSpacing: '1px',
                  fontSize: 'clamp(0.75rem, 1.4vh, 1.1rem)',
                  padding: 'clamp(6px, 1.3vh, 16px)',
                  '&:hover': { backgroundColor: theme.palette.primary.main },
                }}
                variant="contained"
              >
                Log In
              </Button>
              <Typography
                sx={{ fontSize: 'clamp(0.75rem, 1.3vh, 1.1rem)', color: '#1a1a1a', mt: 2, cursor: 'pointer', fontStyle: 'italic' }}
                onClick={() => setForgotPassword(true)}
              >
                Forgot your password?
              </Typography>
            </form>
          ) : (
            <form onSubmit={handleResetSubmit} autoComplete="off">
              <p>Please enter your email. If there is an account associated to this email, a password reset link will be sent.</p>
              <TextInput
                title={'Email'}
                name={'email'}
                handleTextInput={(e: any) => setEmail(e.target.value)}
                value={email}
                required={true}
              />
              <Button
                type="submit"
                sx={{
                  width: '100%',
                  marginTop: '20px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.main },
                }}
                variant="contained"
              >
                Submit
              </Button>
            </form>
          )}
        </Box>
      </Box>

      {/* One platform, end to end */}
      <Box
        sx={{
          backgroundColor: '#f1efe9',
          padding: '50px 60px',
          '@media (max-width: 600px)': { padding: '30px 20px' },
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', letterSpacing: '2px', color: theme.palette.secondary.main, fontWeight: 600, textAlign: 'center', mb: 4 }}>
          ONE PLATFORM, END TO END
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '40px',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>Live inventory & pricing</Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a' }}>Real-time suite availability and holds.</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>CRM & outreach</Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a' }}>Every registrant captured and worked.</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>Portals & deal writing</Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a' }}>Buyer and realtor portals, fast deals.</Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: theme.palette.primary.main,
          padding: '40px 60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
          marginTop: 'auto',
          '@media (max-width: 600px)': { padding: '30px 20px' },
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <img
              src={storeyviewLogo}
              alt="storeyview"
              style={{ height: '20px', filter: 'brightness(0) invert(1)' }}
            />
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#d8d8d0' }}>An RDS company</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: theme.palette.secondary.main, letterSpacing: '1px' }}>STOREYVIEW.COM</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          <Typography
            component="a"
            href="https://rdsbrokerage.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.8rem', color: '#d8d8d0', letterSpacing: '1px', textDecoration: 'none' }}
          >
            PRIVACY
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const RESETPASSWORD = gql`
  mutation userEmailResetPassword($email: String!) {
    userEmailResetPassword(email: $email)
  }
`;

export default withRouter(Login);
